import { decodeTime, encodeTime } from 'ulid';

import { CHANGE_LOG_TTL_DAYS } from './mutation.js';

/**
 * Sync tokens (M4.3.1, M4.3.2).
 *
 * A token names a position in the account's change log: every change with an
 * id greater than it is still to be delivered. Change ids are ULIDs, so the
 * position is also a time, which is what makes the horizon checkable without
 * reading anything.
 *
 * ## The settle window
 *
 * ULIDs are minted in whichever Lambda container handles a request, so two
 * changes a moment apart can commit out of id order — the one with the
 * earlier id landing after a reader has already read past it, and never
 * being delivered. So a read never goes nearer to now than
 * {@link SYNC_SETTLE_MS}: anything younger waits for the next sync. Commits
 * take well under a second; two is the margin.
 */

export const SYNC_SETTLE_MS = 2_000;

/** How far back a token may point: the change log's own lifetime. */
export const SYNC_HORIZON_MS = CHANGE_LOG_TTL_DAYS * 24 * 60 * 60 * 1000;

const TOKEN_VERSION = 1;

export class SyncTokenError extends Error {
  constructor(
    readonly reason: 'malformed' | 'horizon',
    message: string,
  ) {
    super(message);
    this.name = 'SyncTokenError';
  }
}

/** The lowest change id at a moment — every change at or after it is greater. */
export function syncBoundary(at: Date): string {
  return `${encodeTime(Math.max(0, at.getTime()), 10)}${'0'.repeat(16)}`;
}

/** The newest position a read may reach now, per the settle window. */
export function settledBoundary(now: Date): string {
  return syncBoundary(new Date(now.getTime() - SYNC_SETTLE_MS));
}

export function encodeSyncToken(after: string): string {
  return Buffer.from(JSON.stringify({ v: TOKEN_VERSION, a: after }), 'utf8').toString(
    'base64url',
  );
}

/**
 * The position a token names, or an error saying why it cannot be used.
 *
 * `horizon` is the M4.3.2 case: the changes after this token may already
 * have been deleted by the log's TTL, so an incremental sync from it could
 * silently miss some. The client must start again from a full load.
 */
export function decodeSyncToken(token: string, now: Date): string {
  let after: unknown;
  try {
    const parsed = JSON.parse(Buffer.from(token, 'base64url').toString('utf8')) as {
      v?: unknown;
      a?: unknown;
    };
    if (parsed.v !== TOKEN_VERSION) throw new Error('version');
    after = parsed.a;
  } catch {
    throw new SyncTokenError('malformed', 'That is not a sync token');
  }

  if (typeof after !== 'string' || !/^[0-9A-HJKMNP-TV-Z]{26}$/.test(after)) {
    throw new SyncTokenError('malformed', 'That is not a sync token');
  }

  if (now.getTime() - decodeTime(after) > SYNC_HORIZON_MS) {
    throw new SyncTokenError(
      'horizon',
      'This sync position is older than the change history kept; load everything again.',
    );
  }

  return after;
}
